import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import BrandLogo from "@/components/BrandLogos";
import {
  CORRIDORS, DEPOSIT_METHODS, RECEPTION_METHODS,
  formatAmount, formatDate,
} from "@/lib/constants";
import { toast } from "sonner";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle,
  FileImage, Clock, ArrowRight, Loader2, Send as SendIcon,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AgentValidate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(null);
  const [acting, setActing] = useState(null);
  const [done, setDone] = useState(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError("Aucun token fourni dans le lien.");
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get(`/agent-link/info`, { params: { token } });
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled) {
          setError(formatApiError(e?.response?.data?.detail) || "Lien invalide ou expiré.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const doAction = async (decision, reasonText) => {
    setActing(decision);
    try {
      const body = { token, decision };
      if (decision === "reject") body.reason = reasonText;
      const { data } = await api.post(`/agent-link/action`, body);
      setDone({ status: data.status });
      toast.success(
        decision === "validate"
          ? "Transaction validée et marquée envoyée."
          : "Transaction rejetée."
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="min-h-screen sl-hero-bg">
      <div className="pt-10 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="flex items-center gap-3 mb-8">
            <span
              className="w-11 h-11 rounded-full grid place-items-center text-white"
              style={{ background: "var(--sl-primary-dark)" }}
            >
              <ShieldCheck size={20} />
            </span>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">
                Espace agent
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold">Validation sécurisée</h1>
            </div>
          </header>

          {loading && (
            <div className="sl-card flex items-center gap-3 text-gray-600" data-testid="agent-loading">
              <Loader2 className="animate-spin" size={18} /> Vérification du lien…
            </div>
          )}

          {!loading && error && (
            <div className="sl-card border-red-200" data-testid="agent-link-error">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full grid place-items-center bg-red-50 text-red-600 flex-shrink-0">
                  <AlertTriangle size={18} />
                </span>
                <div className="flex-1">
                  <h2 className="font-heading text-lg font-semibold">Lien indisponible</h2>
                  <p className="text-sm text-gray-600 mt-1">{error}</p>
                  <p className="text-xs text-gray-500 mt-3">
                    Les liens de validation expirent au bout de 3 heures pour des raisons de sécurité.
                    Demandez à l'expéditeur ou à l'administrateur de relancer la notification.
                  </p>
                  <Link to="/" className="sl-btn-outline mt-5 inline-flex">Accueil</Link>
                </div>
              </div>
            </div>
          )}

          {!loading && info && done && (
            <div className="sl-card" data-testid="agent-action-done">
              <div className="flex items-start gap-3">
                <span
                  className={`w-10 h-10 rounded-full grid place-items-center flex-shrink-0 ${
                    done.status === "envoye"
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {done.status === "envoye" ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
                </span>
                <div>
                  <h2 className="font-heading text-xl font-semibold">
                    {done.status === "envoye" ? "Transaction validée" : "Transaction rejetée"}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    La référence{" "}
                    <span className="font-mono font-semibold">{info.transaction.code}</span> a été{" "}
                    {done.status === "envoye" ? "marquée comme " : ""}
                    <span className="font-semibold">
                      {done.status === "envoye" ? "envoyée" : "annulée"}
                    </span>{" "}
                    par <span className="font-semibold">{info.agent_name}</span>.
                  </p>
                  <p className="text-xs text-gray-500 mt-3">
                    L'administrateur prendra la suite si nécessaire. Merci.
                  </p>
                  <Link
                    to={`/suivi?code=${info.transaction.code}`}
                    className="sl-btn-primary mt-5 inline-flex"
                    data-testid="agent-done-track-link"
                  >
                    Voir la fiche de suivi <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {!loading && info && !done && (
            <RecapCard
              info={info}
              acting={acting}
              rejectMode={rejectMode}
              setRejectMode={setRejectMode}
              reason={reason}
              setReason={setReason}
              onAction={doAction}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function RecapCard({ info, acting, rejectMode, setRejectMode, reason, setReason, onAction }) {
  const tx = info.transaction;
  const c = CORRIDORS[tx.corridor || "tg_ga"];
  const proofUrl = tx.proof_path ? `${API_BASE}/transactions/${tx.code}/proof` : "";
  const alreadyHandled = tx.status !== "en_attente";

  return (
    <div className="sl-card" data-testid="agent-validate-card">
      {/* Agent identity banner */}
      <div
        className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2 mb-5"
        data-testid="agent-identity-banner"
      >
        <ShieldCheck size={16} />
        <span>
          Vous agissez en tant que{" "}
          <span className="font-semibold">{info.agent_name}</span> · lien valable jusqu'à{" "}
          <span className="font-mono text-xs">{formatDate(info.expires_at)}</span>
        </span>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-500">Référence</div>
          <div className="font-mono text-2xl font-bold" data-testid="agent-tx-code">{tx.code}</div>
          <div className="text-xs text-gray-500 mt-1">
            {c?.flagFrom} {c?.label} {c?.flagTo}
          </div>
        </div>
        <StatusBadge status={tx.status} cancellationReason={tx.cancellation_reason} />
      </div>

      {alreadyHandled && (
        <div
          className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2"
          data-testid="agent-already-handled"
        >
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>Cette transaction a déjà été traitée. Aucune action n'est possible via ce lien.</span>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-3 gap-4 text-center">
        <Box label="Vous recevez (au dépôt)" value={formatAmount(tx.amount_xof, tx.source_currency || "XOF")} />
        <Box label="Frais Swiftlink-Africa" value={formatAmount(tx.fee, tx.source_currency || "XOF")} accent />
        <Box
          label="À envoyer au bénéficiaire"
          value={formatAmount(tx.amount_xaf, tx.target_currency || "XAF")}
          primary
        />
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6 text-sm">
        <div>
          <h4 className="font-heading font-semibold mb-2">Expéditeur</h4>
          <div className="text-gray-700">{tx.sender_name}</div>
          <div className="text-gray-500">{tx.sender_phone}</div>
          {tx.sender_email && <div className="text-gray-500">{tx.sender_email}</div>}
          <div className="mt-3 flex items-center gap-2 text-gray-500">
            <BrandLogo method={tx.deposit_method} size={28} />
            <span>
              Dépôt :{" "}
              <span className="font-medium text-gray-700">
                {DEPOSIT_METHODS[tx.deposit_method]?.label}
              </span>
            </span>
          </div>
        </div>
        <div>
          <h4 className="font-heading font-semibold mb-2">Bénéficiaire</h4>
          <div className="text-gray-700">{tx.beneficiary_name}</div>
          {tx.beneficiary_phone && <div className="text-gray-500">{tx.beneficiary_phone}</div>}
          {tx.beneficiary_email && <div className="text-gray-500">{tx.beneficiary_email}</div>}
          {tx.beneficiary_account && (
            <div className="text-gray-500 font-mono text-xs break-all">{tx.beneficiary_account}</div>
          )}
          <div className="mt-3 flex items-center gap-2 text-gray-500">
            <BrandLogo method={tx.reception_method} size={28} />
            <span>
              Réception :{" "}
              <span className="font-medium text-gray-700">
                {RECEPTION_METHODS[tx.reception_method]?.label}
              </span>
            </span>
          </div>
        </div>
      </div>

      {proofUrl && (
        <div className="mt-6" data-testid="agent-proof-block">
          <h4 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <FileImage size={16} className="text-[var(--sl-primary)]" /> Capture du dépôt
          </h4>
          <a href={proofUrl} target="_blank" rel="noreferrer" className="block group">
            <img
              src={proofUrl}
              alt="Capture du dépôt"
              className="max-h-[360px] rounded-xl border bg-white object-contain mx-auto group-hover:opacity-90 transition"
              style={{ borderColor: "var(--sl-border)" }}
              data-testid="agent-proof-image"
            />
            <div className="mt-2 text-xs text-gray-500 text-center">Cliquez pour ouvrir en plein écran</div>
          </a>
        </div>
      )}

      <div className="mt-6">
        <h4 className="font-heading font-semibold mb-2 text-sm flex items-center gap-2 text-gray-500">
          <Clock size={14} /> Reçue {formatDate(tx.created_at)}
        </h4>
      </div>

      {!alreadyHandled && (
        <div className="mt-6 pt-6 border-t border-gray-100">
          {!rejectMode ? (
            <div className="flex flex-wrap gap-3 items-center" data-testid="agent-actions-row">
              <button
                onClick={() => onAction("validate")}
                disabled={!!acting}
                className="sl-btn-primary"
                data-testid="agent-validate-btn"
              >
                <CheckCircle2 size={16} />
                {acting === "validate" ? "Validation…" : "Valider et marquer envoyé"}
              </button>
              <button
                onClick={() => setRejectMode(true)}
                disabled={!!acting}
                className="sl-btn-outline"
                data-testid="agent-reject-btn"
              >
                <XCircle size={16} /> Rejeter
              </button>
              <div className="text-xs text-gray-500 ml-1 flex items-center gap-1">
                <SendIcon size={12} /> Valider passe directement au statut « Envoyé »
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="agent-reject-form">
              <label className="sl-label">Motif de rejet (visible par l'admin)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="sl-input"
                rows="3"
                placeholder="Ex : capture illisible, montant incorrect, numéro non identifié…"
                data-testid="agent-reject-reason"
              />
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => onAction("reject", reason)}
                  disabled={!reason.trim() || !!acting}
                  className="sl-btn-primary"
                  style={{ background: "#B91C1C" }}
                  data-testid="agent-confirm-reject-btn"
                >
                  <XCircle size={16} /> {acting === "reject" ? "Envoi…" : "Confirmer le rejet"}
                </button>
                <button
                  onClick={() => {
                    setRejectMode(false);
                    setReason("");
                  }}
                  className="sl-btn-outline"
                  disabled={!!acting}
                  data-testid="agent-cancel-reject-btn"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Box({ label, value, primary, accent }) {
  const cls = primary ? "text-[var(--sl-primary)]" : accent ? "text-amber-700" : "text-gray-900";
  return (
    <div className="rounded-xl bg-[var(--sl-bg-alt)] p-4">
      <div className="text-xs uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading text-lg font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
